import { useState, useCallback } from 'react';
import { useApiClient } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';

interface BatchJob {
  id: string;
  action: string;
  status: string;
  total_items: number;
  processed_items: number;
  success_count: number;
  error_count: number;
  created_at: string;
  completed_at?: string;
  result_url?: string;
  envelope_result_url?: string;
}

interface BatchOperationResponse {
  batchId: string;
  jobId: string;
  status: string;
  message: string;
}

interface BatchOperationError {
  error: string;
  details?: string;
}

export const useBatchOperations = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);
  const { token, currentBusiness, isCookieMode } = useAuth();
  const { post, get, delete: del } = useApiClient();

  const executeBatchAction = useCallback(async (
    action: string, 
    itemIds: string[],
    entityType: 'invoices' | 'customers' | 'items' = 'invoices',
    options?: { includeEnvelopes?: boolean }
  ): Promise<BatchOperationResponse> => {
    setIsProcessing(true);
    setCurrentBatchId(null);
    
    if (!currentBusiness) {
      throw new Error('Business context required');
    }
    
    if (!isCookieMode && !token) {
      throw new Error('Authentication required');
    }
    
    try {
      const result = await post<BatchOperationResponse>(`/batch/${entityType}`, { action, itemIds, includeEnvelopes: options?.includeEnvelopes });
      setCurrentBatchId(result.batchId);
      
      return result;
    } catch (error: any) {
      const errorResponse: BatchOperationError = {
        error: error?.message || 'Batch operation failed',
      };
      throw errorResponse;
    } finally {
      setIsProcessing(false);
    }
  }, [token, currentBusiness, isCookieMode, post]);

  const getBatchJobStatus = useCallback(async (batchId: string): Promise<BatchJob> => {
    if (!currentBusiness) {
      throw new Error('Business context required');
    }
    
    if (!isCookieMode && !token) {
      throw new Error('Authentication required');
    }
    
    try {
      return await get<BatchJob>(`/batch/${batchId}`);
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to get batch job status');
    }
  }, [token, currentBusiness, isCookieMode, get]);

  const undoBatchOperation = useCallback(async (batchId: string): Promise<BatchOperationResponse> => {
    if (!currentBusiness) {
      throw new Error('Business context required');
    }
    
    if (!isCookieMode && !token) {
      throw new Error('Authentication required');
    }
    
    try {
      return await post<BatchOperationResponse>(`/batch/undo/${batchId}`, {});
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to undo batch operation');
    }
  }, [token, currentBusiness, isCookieMode, post]);

  const cancelBatchOperation = useCallback(async (batchId: string): Promise<void> => {
    if (!currentBusiness) {
      throw new Error('Business context required');
    }
    
    if (!isCookieMode && !token) {
      throw new Error('Authentication required');
    }
    
    try {
      await del<void>(`/batch/${batchId}`);
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to cancel batch operation');
    }
  }, [token, currentBusiness, isCookieMode, del]);

  const generateExport = useCallback(async (
    batchId: string, 
    fileType: 'pdf' | 'csv' = 'pdf',
    includeEnvelopes?: boolean
  ): Promise<{ resultUrl: string; envelopeResultUrl?: string }> => {
    if (!currentBusiness) {
      throw new Error('Business context required');
    }
    
    if (!isCookieMode && !token) {
      throw new Error('Authentication required');
    }
    
    try {
      const response = await post<{ resultUrl: string; envelopeResultUrl?: string }>(`/batch/export/${batchId}`, { fileType, includeEnvelopes });
      return { resultUrl: response.resultUrl, envelopeResultUrl: (response as any).envelopeResultUrl };
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to generate export');
    }
  }, [token, currentBusiness, isCookieMode, post]);

  const getBatchJobs = useCallback(async (params?: {
    status?: string;
    action?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ batchJobs: BatchJob[]; pagination: any }> => {
    if (!currentBusiness) {
      throw new Error('Business context required');
    }
    
    if (!isCookieMode && !token) {
      throw new Error('Authentication required');
    }
    
    try {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.append('status', params.status);
      if (params?.action) queryParams.append('action', params.action);
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.offset) queryParams.append('offset', params.offset.toString());
      return await get<{ batchJobs: BatchJob[]; pagination: any }>(`/batch?${queryParams.toString()}`);
    } catch (error: any) {
      throw new Error(error?.message || 'Failed to get batch jobs');
    }
  }, [token, currentBusiness, isCookieMode, get]);

  return {
    executeBatchAction,
    getBatchJobStatus,
    undoBatchOperation,
    cancelBatchOperation,
    generateExport,
    getBatchJobs,
    isProcessing,
    currentBatchId
  };
}; 